// src/services/usuarioService.js

import api from './api';

/**
 * 👥 Servicio de Gestión de Usuarios
 * Todos los métodos requieren autenticación
 * Solo ADMIN puede gestionar usuarios
 */

const usuarioService = {
  /**
   * 📋 Obtener todos los usuarios (solo ADMIN)
   * @param {Object} filters - Filtros opcionales (nombre, rol)
   * @returns {Promise}
   */
  obtenerUsuarios: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      
      if (filters.nombre) {
        params.append('search', filters.nombre);
      }
      
      if (filters.rol) {
        params.append('rol', filters.rol);
      }
      
      const response = await api.get(`/auth/users/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener usuarios:', error.response?.data);
      throw new Error(
        error.response?.data?.detail || 'Error al obtener usuarios'
      );
    }
  },

  /**
   * 👤 Obtener un usuario específico (solo ADMIN)
   * @param {number} usuarioId - ID del usuario
   * @returns {Promise}
   */
  obtenerUsuario: async (usuarioId) => {
    try {
      const response = await api.get(`/auth/users/${usuarioId}/`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener usuario:', error.response?.data);
      throw new Error('Error al obtener el usuario');
    }
  },

  /**
   * ➕ Crear nuevo usuario (solo ADMIN)
   * @param {Object} usuarioData - Datos del usuario
   * @returns {Promise}
   */
  crearUsuario: async (usuarioData) => {
    try {
      const response = await api.post('/auth/register/', usuarioData);
      return response.data;
    } catch (error) {
      console.error('Error al crear usuario:', error.response?.data);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.errors ||
        'Error al crear usuario'
      );
    }
  },

  /**
   * ✏️ Actualizar usuario (solo ADMIN)
   * @param {number} usuarioId - ID del usuario
   * @param {Object} usuarioData - Datos a actualizar
   * @returns {Promise}
   */
  actualizarUsuario: async (usuarioId, usuarioData) => {
    try {
      const response = await api.patch(`/auth/users/${usuarioId}/`, usuarioData);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar usuario:', error.response?.data);
      throw new Error('Error al actualizar el usuario');
    }
  },

  /**
   * 🗑️ Eliminar usuario (solo ADMIN)
   * @param {number} usuarioId - ID del usuario
   * @returns {Promise}
   */
  eliminarUsuario: async (usuarioId) => {
    try {
      const response = await api.delete(`/auth/users/${usuarioId}/`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar usuario:', error.response?.data);
      throw new Error('Error al eliminar el usuario');
    }
  },

  /**
   * 🔐 Cambiar rol de usuario (solo ADMIN)
   * @param {number} usuarioId - ID del usuario
   * @param {string} nuevoRol - Nuevo rol (ADMIN, SECRETARIA, REVISOR, SOCIO)
   * @returns {Promise}
   */
  cambiarRol: async (usuarioId, nuevoRol) => {
    try {
      const response = await api.patch(`/auth/users/${usuarioId}/`, {
        rol_principal: nuevoRol,
      });
      return response.data;
    } catch (error) {
      console.error('Error al cambiar rol:', error.response?.data);
      throw new Error('Error al cambiar el rol del usuario');
    }
  },

  /**
   * 👔 Cambiar estado de staff (solo ADMIN)
   * @param {number} usuarioId - ID del usuario
   * @param {boolean} isStaff - true para marcar como staff, false para quitarlo
   * @returns {Promise}
   */
  cambiarEstadoStaff: async (usuarioId, isStaff) => {
    try {
      const response = await api.patch(`/auth/users/${usuarioId}/`, {
        is_staff: isStaff,
      });
      return response.data;
    } catch (error) {
      console.error('Error al cambiar estado staff:', error.response?.data);
      throw new Error('Error al cambiar el estado de staff');
    }
  },

  /**
   * 👔 Alternar estado staff usando endpoint dedicado
   * @param {number} usuarioId - ID del usuario
   * @returns {Promise}
   */
  toggleStaff: async (usuarioId) => {
    try {
      const response = await api.post(`/auth/users/${usuarioId}/toggle-staff/`);
      return response.data;
    } catch (error) {
      console.error('Error al alternar estado staff:', error.response?.data);
      throw new Error('Error al actualizar estado staff');
    }
  },

  /**
   * 📚 Obtener catálogo de roles
   * @returns {Promise}
   */
  obtenerRoles: async () => {
    try {
      const response = await api.get('/roles/');
      return response.data;
    } catch (error) {
      console.error('Error al obtener roles:', error.response?.data);
      throw new Error('Error al obtener roles');
    }
  },

  /**
   * 🏷️ Asignar rol de negocio a usuario auth
   * @param {number} usuarioId
   * @param {number} rolId
   * @returns {Promise}
   */
  asignarRolAuthUsuario: async (usuarioId, rolId) => {
    try {
      const response = await api.post('/auth-user-roles/', {
        user: usuarioId,
        rol: rolId,
      });
      return response.data;
    } catch (error) {
      console.error('Error al asignar rol auth_user:', error.response?.data);
      throw new Error('Error al asignar rol al usuario');
    }
  },

  /**
   * 🔓 Cambiar estado activo de usuario (solo ADMIN)
   * @param {number} usuarioId - ID del usuario
   * @param {boolean} isActive - true para activar, false para desactivar
   * @returns {Promise}
   */
  cambiarEstadoActivo: async (usuarioId, isActive) => {
    try {
      const response = await api.patch(`/auth/users/${usuarioId}/`, {
        is_active: isActive,
      });
      return response.data;
    } catch (error) {
      console.error('Error al cambiar estado activo:', error.response?.data);
      throw new Error('Error al cambiar el estado activo');
    }
  },

  /**
   * 📋 Obtener roles disponibles
   * @returns {Array}
   */
  obtenerRolesDisponibles: () => {
    return [
      { value: 'ADMIN', label: 'Administrador' },
      { value: 'SECRETARIA', label: 'Secretaria' },
      { value: 'REVISOR', label: 'Revisor' },
      { value: 'SOCIO', label: 'Socio' },
    ];
  },

  /**
   * 🔍 Buscar socios para asignar como usuarios
   * @param {string} query - Término de búsqueda
   * @param {Object} options - Opciones de paginación
   * @param {number} options.page - Página actual
   * @param {number} options.pageSize - Tamaño de página
   * @returns {Promise}
   */
  buscarSocios: async (query = '', options = {}) => {
    try {
      const { page = 1, pageSize = 100 } = options;
      const params = new URLSearchParams();
      if (query) {
        params.append('search', query);
      }
      if (page) {
        params.append('page', page);
      }
      if (pageSize) {
        params.append('page_size', pageSize);
      }
      const response = await api.get(`/socios/?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error al buscar socios:', error.response?.data);
      throw new Error('Error al buscar socios');
    }
  },
};

export default usuarioService;
